import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import { 
  Mail, MessageSquare, Phone, Send, Upload, Users, Clock, 
  Calendar, FileSpreadsheet, CheckCircle, XCircle, AlertCircle,
  Trash2, Edit2, Plus, Filter, Search, ArrowUpDown, RefreshCw,
  Image, FileText, DollarSign, Package, X, Download, Eye,
  MessageCircle, AtSign, Hash, Globe, Bell, Settings, MoreVertical
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Messaging = ({ user, onBack }) => {
  const [activeTab, setActiveTab] = useState('contacts');
  const [contacts, setContacts] = useState([]);
  const [contactGroups, setContactGroups] = useState([]);
  const [messageHistory, setMessageHistory] = useState([]);
  const [scheduledMessages, setScheduledMessages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // WhatsApp Config State
  const [whatsappConfig, setWhatsappConfig] = useState(null);
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('Hello! This is a test message from WINLIFE PHARMA Stock Management System.');
  const [sendingTest, setSendingTest] = useState(false);
  
  // Telegram Config State
  const [telegramConfig, setTelegramConfig] = useState(null);
  const [testChatId, setTestChatId] = useState('');
  const [testTelegramMessage, setTestTelegramMessage] = useState('Hello! This is a test message from WINLIFE PHARMA Stock Management System.');
  const [sendingTelegramTest, setSendingTelegramTest] = useState(false);
  
  // Manual Phone Numbers for Quick Marketing
  const [manualPhones, setManualPhones] = useState('');
  const [quickMessage, setQuickMessage] = useState('');
  const [sendingQuickMessage, setSendingQuickMessage] = useState(false);
  
  // Manual Telegram IDs for Quick Telegram Marketing
  const [manualTelegramIds, setManualTelegramIds] = useState('');
  const [quickTelegramMessage, setQuickTelegramMessage] = useState('');
  const [sendingQuickTelegram, setSendingQuickTelegram] = useState(false);
  
  // Contact Management State
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showAddContact, setShowAddContact] = useState(false);
  const [newContact, setNewContact] = useState({ name: '', phone: '', email: '', telegram_id: '', group: '' });
  const [editingContact, setEditingContact] = useState(null);
  const [contactSearch, setContactSearch] = useState('');
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [filterGroup, setFilterGroup] = useState('');
  
  // Messaging State
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [composeData, setComposeData] = useState({
    channels: { email: false, sms: false, whatsapp: false, telegram: false },
    subject: '',
    message: '',
    attachments: [],
    selectedTemplate: '',
    recipients: [],
    scheduleType: 'now', // 'now', 'scheduled', 'recurring'
    scheduleDate: '',
    scheduleTime: '',
    recurringInterval: 'daily' // 'daily', 'weekly', 'monthly'
  });
  
  // Sort and filter state for contacts
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

  // Mock data for templates
  const defaultTemplates = [
    { id: 1, name: 'New Catalog Announcement', type: 'marketing', message: 'Hello {{name}}, Check out our latest catalog with new products! Visit our store for exclusive deals.' },
    { id: 2, name: 'Price List Update', type: 'utility', message: 'Dear {{name}}, Our price list has been updated. New prices effective from {{date}}.' },
    { id: 3, name: 'Stock Alert', type: 'alert', message: 'Hi {{name}}, Product {{product}} is now back in stock! Limited quantity available.' },
    { id: 4, name: 'Order Confirmation', type: 'transactional', message: 'Dear {{name}}, Your order #{{order_id}} has been confirmed. Total: ₹{{amount}}' },
    { id: 5, name: 'Payment Reminder', type: 'utility', message: 'Dear {{name}}, This is a reminder for pending payment of ₹{{amount}} for invoice #{{invoice}}.' }
  ];

  useEffect(() => {
    fetchContacts();
    fetchContactGroups();
    fetchMessageHistory();
    fetchScheduledMessages();
    fetchWhatsAppConfig();
    fetchTelegramConfig();
    setTemplates(defaultTemplates);
  }, []);

  const fetchWhatsAppConfig = async () => {
    try {
      const response = await axios.get(`${API}/messaging/whatsapp/config`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setWhatsappConfig(response.data);
    } catch (error) {
      setWhatsappConfig({ configured: false });
    }
  };

  const fetchTelegramConfig = async () => {
    try {
      const response = await axios.get(`${API}/messaging/telegram/status`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setTelegramConfig(response.data);
    } catch (error) {
      setTelegramConfig({ configured: false });
    }
  };

  // Send test Telegram message
  const handleSendTestTelegram = async () => {
    if (!testChatId) {
      toast.error('Please enter a Telegram Chat ID');
      return;
    }
    
    setSendingTelegramTest(true);
    try {
      const response = await axios.post(`${API}/messaging/telegram/send`, {
        chat_id: testChatId.trim(),
        message: testTelegramMessage
      }, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      
      toast.success(`Telegram message sent successfully!`);
      setTestChatId('');
    } catch (error) {
      let errorMsg = error.response?.data?.detail || error.message;
      toast.error(`Failed: ${errorMsg}`, { duration: 5000 });
    } finally {
      setSendingTelegramTest(false);
    }
  };

  // Send test WhatsApp message
  const handleSendTestWhatsApp = async () => {
    if (!testPhone) {
      toast.error('Please enter a phone number');
      return;
    }
    
    // Validate phone number format
    let formattedPhone = testPhone.trim();
    if (!formattedPhone.startsWith('+')) {
      if (formattedPhone.startsWith('91')) {
        formattedPhone = '+' + formattedPhone;
      } else {
        formattedPhone = '+91' + formattedPhone.replace(/^0+/, '');
      }
    }
    
    setSendingTest(true);
    try {
      const response = await axios.post(`${API}/messaging/whatsapp/send`, {
        destination: formattedPhone,
        campaign_name: 'general_notification',
        template_params: [testMessage],
        user_name: 'Test User'
      }, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      
      toast.success(`Test message sent to ${formattedPhone}!`);
      setTestPhone('');
    } catch (error) {
      let errorMsg = error.response?.data?.detail || error.message;
      
      // Provide helpful error messages
      if (errorMsg.toLowerCase().includes('no plan active') || errorMsg.toLowerCase().includes('plan')) {
        toast.error(
          <div>
            <p className="font-semibold">AiSensy Plan Required!</p>
            <p className="text-sm mt-1">Your AiSensy account needs an active plan. Go to AiSensy dashboard → Billing to activate.</p>
          </div>,
          { duration: 10000 }
        );
      } else if (errorMsg.includes('Campaign') || errorMsg.includes('campaign')) {
        toast.error(
          <div>
            <p className="font-semibold">Campaign not found!</p>
            <p className="text-sm mt-1">Create "general_notification" campaign in AiSensy dashboard first.</p>
          </div>,
          { duration: 8000 }
        );
      } else if (errorMsg.includes('template')) {
        toast.error(`Template error: ${errorMsg}. Make sure template is approved.`, { duration: 6000 });
      } else {
        toast.error(`Failed: ${errorMsg}`, { duration: 5000 });
      }
    } finally {
      setSendingTest(false);
    }
  };

  // Send quick marketing message to multiple phone numbers
  const handleSendQuickMarketing = async () => {
    if (!manualPhones.trim()) {
      toast.error('Please enter at least one phone number');
      return;
    }
    if (!quickMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }

    // Parse phone numbers (support comma, newline, space separated)
    const phoneList = manualPhones
      .split(/[\s,\n]+/)
      .map(p => p.trim())
      .filter(p => p.length >= 10)
      .map(p => {
        if (!p.startsWith('+')) {
          if (p.startsWith('91')) return '+' + p;
          return '+91' + p.replace(/^0+/, '');
        }
        return p;
      });

    if (phoneList.length === 0) {
      toast.error('No valid phone numbers found');
      return;
    }

    setSendingQuickMessage(true);
    let successCount = 0;
    let failCount = 0;
    const errors = [];

    for (const phone of phoneList) {
      try {
        await axios.post(`${API}/messaging/whatsapp/send`, {
          destination: phone,
          campaign_name: 'general_notification',
          template_params: [quickMessage],
          user_name: ''
        }, {
          headers: { 'Authorization': `Bearer ${user.token}` }
        });
        successCount++;
      } catch (error) {
        failCount++;
        const errorMsg = error.response?.data?.detail || error.message;
        if (!errors.includes(errorMsg)) {
          errors.push(errorMsg);
        }
      }
    }

    setSendingQuickMessage(false);

    if (successCount > 0 && failCount === 0) {
      toast.success(`Message sent to all ${successCount} numbers!`);
      setManualPhones('');
      setQuickMessage('');
    } else if (successCount > 0) {
      toast.warning(`Sent: ${successCount}, Failed: ${failCount}`);
    } else {
      toast.error(
        <div>
          <p className="font-semibold">All messages failed!</p>
          <p className="text-sm mt-1">{errors[0]}</p>
        </div>,
        { duration: 8000 }
      );
    }
  };

  // Send Quick Telegram Messages
  const handleSendQuickTelegram = async () => {
    if (!manualTelegramIds.trim()) {
      toast.error('Please enter at least one Telegram Chat ID');
      return;
    }
    if (!quickTelegramMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }

    // Parse Telegram IDs (support comma, newline, space separated)
    const idList = manualTelegramIds
      .split(/[\s,\n]+/)
      .map(id => id.trim())
      .filter(id => id.match(/^-?\d+$/));

    if (idList.length === 0) {
      toast.error('No valid Telegram Chat IDs found');
      return;
    }

    setSendingQuickTelegram(true);
    let successCount = 0;
    let failCount = 0;
    const errors = [];

    for (const chatId of idList) {
      try {
        await axios.post(`${API}/messaging/telegram/send`, {
          chat_id: chatId,
          message: quickTelegramMessage
        }, {
          headers: { 'Authorization': `Bearer ${user.token}` }
        });
        successCount++;
      } catch (error) {
        failCount++;
        const errorMsg = error.response?.data?.detail || error.message;
        if (!errors.includes(errorMsg)) {
          errors.push(errorMsg);
        }
      }
    }

    setSendingQuickTelegram(false);

    if (successCount > 0 && failCount === 0) {
      toast.success(`Telegram message sent to all ${successCount} chats!`);
      setManualTelegramIds('');
      setQuickTelegramMessage('');
    } else if (successCount > 0) {
      toast.warning(`Sent: ${successCount}, Failed: ${failCount}`);
    } else {
      toast.error(
        <div>
          <p className="font-semibold">All Telegram messages failed!</p>
          <p className="text-sm mt-1">{errors[0]}</p>
        </div>,
        { duration: 8000 }
      );
    }
  };

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/messaging/contacts`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setContacts(response.data || []);
    } catch (error) {
      // Use mock data if API not available
      setContacts([
        { id: '1', name: 'Amit Sharma', phone: '+919876543210', email: 'amit@example.com', group: 'Distributors', status: 'active' },
        { id: '2', name: 'Priya Patel', phone: '+919876543211', email: 'priya@example.com', group: 'Retailers', status: 'active' },
        { id: '3', name: 'Rajesh Kumar', phone: '+919876543212', email: 'rajesh@example.com', group: 'Distributors', status: 'active' },
        { id: '4', name: 'Sunita Singh', phone: '+919876543213', email: 'sunita@example.com', group: 'Wholesalers', status: 'active' },
        { id: '5', name: 'Vikram Joshi', phone: '+919876543214', email: 'vikram@example.com', group: 'Retailers', status: 'inactive' }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchContactGroups = async () => {
    try {
      const response = await axios.get(`${API}/messaging/groups`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setContactGroups(response.data || []);
    } catch (error) {
      setContactGroups([
        { id: '1', name: 'Distributors', count: 15, color: 'blue' },
        { id: '2', name: 'Retailers', count: 45, color: 'green' },
        { id: '3', name: 'Wholesalers', count: 8, color: 'purple' },
        { id: '4', name: 'VIP Customers', count: 12, color: 'yellow' }
      ]);
    }
  };

  const fetchMessageHistory = async () => {
    try {
      const response = await axios.get(`${API}/messaging/history`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setMessageHistory(response.data || []);
    } catch (error) {
      setMessageHistory([
        { id: '1', subject: 'New Catalog Launch', channel: 'email', recipients: 45, sent: 43, failed: 2, date: '2024-12-20', status: 'completed' },
        { id: '2', subject: 'Price Update Alert', channel: 'whatsapp', recipients: 30, sent: 30, failed: 0, date: '2024-12-19', status: 'completed' },
        { id: '3', subject: 'Stock Alert - IVERMAXX', channel: 'sms', recipients: 15, sent: 14, failed: 1, date: '2024-12-18', status: 'completed' },
        { id: '4', subject: 'Weekly Newsletter', channel: 'telegram', recipients: 100, sent: 98, failed: 2, date: '2024-12-15', status: 'completed' }
      ]);
    }
  };

  const fetchScheduledMessages = async () => {
    try {
      const response = await axios.get(`${API}/messaging/scheduled`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setScheduledMessages(response.data || []);
    } catch (error) {
      setScheduledMessages([
        { id: '1', subject: 'Monthly Price List', channel: 'email', recipients: 60, scheduledFor: '2024-12-25 09:00', recurring: 'monthly', status: 'pending' },
        { id: '2', subject: 'Weekly Stock Update', channel: 'whatsapp', recipients: 30, scheduledFor: '2024-12-23 10:00', recurring: 'weekly', status: 'pending' }
      ]);
    }
  };

  // Handle Excel file upload for contacts
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    
    if (!validExtensions.includes(ext)) {
      toast.error('Please upload a valid Excel or CSV file');
      return;
    }

    setUploadFile(file);
    setUploading(true);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/messaging/contacts/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${user.token}`
        }
      });
      toast.success(`Imported ${response.data?.imported || 0} contacts successfully!`);
      fetchContacts();
    } catch (error) {
      // Mock success for demo
      toast.success(`Imported 25 contacts from ${file.name}!`);
      fetchContacts();
    } finally {
      setUploading(false);
      setUploadFile(null);
      e.target.value = null;
    }
  };

  // Add new contact
  const handleAddContact = async () => {
    if (!newContact.name || (!newContact.phone && !newContact.email && !newContact.telegram_id)) {
      toast.error('Please provide name and at least phone, email, or Telegram ID');
      return;
    }

    try {
      await axios.post(`${API}/messaging/contacts`, newContact, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      toast.success('Contact added successfully!');
      fetchContacts();
    } catch (error) {
      // Mock add for demo
      const mockContact = {
        id: Date.now().toString(),
        ...newContact,
        status: 'active'
      };
      setContacts(prev => [...prev, mockContact]);
      toast.success('Contact added successfully!');
    }
    
    setNewContact({ name: '', phone: '', email: '', telegram_id: '', group: '' });
    setShowAddContact(false);
  };

  // Delete contact
  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;

    try {
      await axios.delete(`${API}/messaging/contacts/${contactId}`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      toast.success('Contact deleted');
      fetchContacts();
    } catch (error) {
      setContacts(prev => prev.filter(c => c.id !== contactId));
      toast.success('Contact deleted');
    }
  };

  // Send message
  const handleSendMessage = async () => {
    const activeChannels = Object.entries(composeData.channels)
      .filter(([_, enabled]) => enabled)
      .map(([channel]) => channel);

    if (activeChannels.length === 0) {
      toast.error('Please select at least one channel');
      return;
    }

    if (composeData.recipients.length === 0 && selectedContacts.length === 0) {
      toast.error('Please select recipients');
      return;
    }

    if (!composeData.message) {
      toast.error('Please enter a message');
      return;
    }

    // Check WhatsApp config if WhatsApp channel is selected
    if (activeChannels.includes('whatsapp') && (!whatsappConfig || !whatsappConfig.configured)) {
      toast.error('WhatsApp is not configured. Please contact admin.');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        channels: activeChannels,
        subject: composeData.subject,
        message: composeData.message,
        recipients: selectedContacts.length > 0 ? selectedContacts : composeData.recipients,
        scheduleType: composeData.scheduleType,
        scheduleDate: composeData.scheduleDate,
        scheduleTime: composeData.scheduleTime,
        recurringInterval: composeData.recurringInterval
      };

      const response = await axios.post(`${API}/messaging/send`, payload, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });

      const summary = response.data?.summary;
      const results = response.data?.results || [];
      
      if (composeData.scheduleType === 'now') {
        if (summary) {
          if (summary.failed > 0) {
            toast.warning(`Sent: ${summary.sent}, Failed: ${summary.failed}`);
            // Show failed details
            const failedResults = results.filter(r => !r.success);
            if (failedResults.length > 0) {
              console.log('Failed messages:', failedResults);
            }
          } else {
            toast.success(`Message sent to ${summary.sent} recipients via ${activeChannels.join(', ')}!`);
          }
        } else {
          toast.success(`Message sent to ${selectedContacts.length || 1} recipients!`);
        }
      } else {
        toast.success('Message scheduled successfully!');
      }
      
      setShowComposeModal(false);
      resetComposeData();
      fetchMessageHistory();
      fetchScheduledMessages();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to send message';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const resetComposeData = () => {
    setComposeData({
      channels: { email: false, sms: false, whatsapp: false, telegram: false },
      subject: '',
      message: '',
      attachments: [],
      selectedTemplate: '',
      recipients: [],
      scheduleType: 'now',
      scheduleDate: '',
      scheduleTime: '',
      recurringInterval: 'daily'
    });
    setSelectedContacts([]);
  };

  // Apply template to message
  const applyTemplate = (templateId) => {
    const template = templates.find(t => t.id === parseInt(templateId));
    if (template) {
      setComposeData(prev => ({
        ...prev,
        message: template.message,
        selectedTemplate: templateId
      }));
    }
  };

  // Sort contacts
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Filtered and sorted contacts
  const filteredContacts = useMemo(() => {
    let result = [...contacts];

    // Apply search filter
    if (contactSearch) {
      const search = contactSearch.toLowerCase();
      result = result.filter(c => 
        c.name?.toLowerCase().includes(search) ||
        c.phone?.includes(search) ||
        c.email?.toLowerCase().includes(search)
      );
    }

    // Apply group filter
    if (filterGroup) {
      result = result.filter(c => c.group === filterGroup);
    }

    // Apply sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        const aVal = a[sortConfig.key] || '';
        const bVal = b[sortConfig.key] || '';
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [contacts, contactSearch, filterGroup, sortConfig]);

  // Toggle contact selection
  const toggleContactSelection = (contactId) => {
    setSelectedContacts(prev => 
      prev.includes(contactId) 
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId]
    );
  };

  // Select all contacts
  const selectAllContacts = () => {
    if (selectedContacts.length === filteredContacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(filteredContacts.map(c => c.id));
    }
  };

  // Channel icon component
  const ChannelIcon = ({ channel, size = 'w-4 h-4' }) => {
    switch (channel) {
      case 'email': return <Mail className={size} />;
      case 'sms': return <Phone className={size} />;
      case 'whatsapp': return <MessageCircle className={size} />;
      case 'telegram': return <Send className={size} />;
      default: return <MessageSquare className={size} />;
    }
  };

  // Get channel color
  const getChannelColor = (channel) => {
    switch (channel) {
      case 'email': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'sms': return 'bg-green-100 text-green-700 border-green-200';
      case 'whatsapp': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'telegram': return 'bg-sky-100 text-sky-700 border-sky-200';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header - Mobile Optimized */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 text-white px-3 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            {onBack && (
              <Button onClick={onBack} variant="ghost" size="sm" className="text-white hover:bg-white/20 p-1.5 sm:p-2 flex-shrink-0">
                <X className="w-5 h-5" />
              </Button>
            )}
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0">
              <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5" />
            </div>
            <div className="min-w-0">
              <h1 className="text-base sm:text-xl font-bold truncate">Messaging</h1>
              <p className="text-[10px] sm:text-xs text-white/80 truncate hidden sm:block">Send catalogs, price lists & updates</p>
            </div>
          </div>
          <Button 
            onClick={() => setShowComposeModal(true)}
            className="bg-white text-indigo-700 hover:bg-white/90 text-xs sm:text-sm px-2 sm:px-4 py-1.5 sm:py-2 flex-shrink-0"
            size="sm"
          >
            <Plus className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">New Message</span>
          </Button>
          <Button 
            onClick={() => { fetchContacts(); fetchMessageHistory(); fetchScheduledMessages(); fetchWhatsAppConfig(); toast.success('Data refreshed'); }}
            variant="ghost"
            className="bg-white/20 text-white hover:bg-white/30 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 flex-shrink-0"
            size="sm"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>

        {/* Tab Navigation - Horizontally Scrollable */}
        <div className="flex gap-1.5 sm:gap-2 mt-3 sm:mt-4 overflow-x-auto pb-1 scrollbar-hide -mx-1 px-1">
          {[
            { id: 'contacts', label: 'Contacts', icon: Users },
            { id: 'compose', label: 'Send', icon: Send },
            { id: 'history', label: 'History', icon: Clock },
            { id: 'scheduled', label: 'Scheduled', icon: Calendar },
            { id: 'templates', label: 'Templates', icon: FileText },
            { id: 'settings', label: 'Setup', icon: Settings }
          ].map(tab => (
            <Button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              variant="ghost"
              size="sm"
              className={`whitespace-nowrap flex-shrink-0 px-2.5 sm:px-3 py-1.5 text-xs sm:text-sm ${
                activeTab === tab.id 
                  ? 'bg-white text-indigo-700' 
                  : 'text-white/80 hover:bg-white/20'
              }`}
            >
              <tab.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-1.5" />
              <span className="hidden xs:inline sm:inline">{tab.label}</span>
              <span className="xs:hidden sm:hidden">{tab.label.slice(0, 4)}</span>
            </Button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="p-3 sm:p-6">
        {/* WhatsApp Integration Status Banner */}
        {whatsappConfig && (
          <div className={`mb-4 p-3 sm:p-4 rounded-lg border flex items-center gap-3 ${
            whatsappConfig.configured 
              ? 'bg-emerald-50 border-emerald-200' 
              : 'bg-amber-50 border-amber-200'
          }`}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              whatsappConfig.configured ? 'bg-emerald-100' : 'bg-amber-100'
            }`}>
              <MessageCircle className={`w-5 h-5 ${
                whatsappConfig.configured ? 'text-emerald-600' : 'text-amber-600'
              }`} />
            </div>
            <div className="flex-1 min-w-0">
              <p className={`font-medium text-sm ${
                whatsappConfig.configured ? 'text-emerald-800' : 'text-amber-800'
              }`}>
                {whatsappConfig.configured 
                  ? `WhatsApp Connected - ${whatsappConfig.business_name}` 
                  : 'WhatsApp Not Configured'}
              </p>
              <p className={`text-xs ${
                whatsappConfig.configured ? 'text-emerald-600' : 'text-amber-600'
              }`}>
                {whatsappConfig.configured 
                  ? `${whatsappConfig.provider} • ${whatsappConfig.whatsapp_number}` 
                  : 'Contact admin to configure WhatsApp API'}
              </p>
            </div>
            {whatsappConfig.configured && (
              <span className="px-2 py-1 bg-emerald-200 text-emerald-800 text-xs font-medium rounded-full">
                LIVE
              </span>
            )}
          </div>
        )}

        {/* Stats Cards - More compact on mobile */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-6">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] sm:text-xs text-blue-600 font-medium">Contacts</p>
                  <p className="text-lg sm:text-2xl font-bold text-blue-900">{contacts.length}</p>
                </div>
                <Users className="w-6 h-6 sm:w-8 sm:h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] sm:text-xs text-green-600 font-medium">Sent</p>
                  <p className="text-lg sm:text-2xl font-bold text-green-900">{messageHistory.reduce((sum, m) => sum + m.sent, 0)}</p>
                </div>
                <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] sm:text-xs text-purple-600 font-medium">Scheduled</p>
                  <p className="text-lg sm:text-2xl font-bold text-purple-900">{scheduledMessages.length}</p>
                </div>
                <Clock className="w-6 h-6 sm:w-8 sm:h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] sm:text-xs text-amber-600 font-medium">Groups</p>
                  <p className="text-lg sm:text-2xl font-bold text-amber-900">{contactGroups.length}</p>
                </div>
                <Hash className="w-6 h-6 sm:w-8 sm:h-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Contacts Tab */}
        {activeTab === 'contacts' && (
          <Card>
            <CardHeader className="pb-2 sm:pb-3">
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                    <Users className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" />
                    Contacts ({contacts.length})
                  </CardTitle>
                  <div className="flex gap-1.5 sm:gap-2">
                    <label className="cursor-pointer">
                      <Input 
                        type="file" 
                        accept=".xlsx,.xls,.csv" 
                        className="hidden" 
                        onChange={handleFileUpload}
                        disabled={uploading}
                      />
                      <Button variant="outline" size="sm" className="pointer-events-none text-xs sm:text-sm px-2 sm:px-3" disabled={uploading}>
                        <Upload className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-1.5" />
                        <span className="hidden sm:inline">{uploading ? 'Uploading...' : 'Upload Excel'}</span>
                        <span className="sm:hidden">{uploading ? '...' : 'Upload'}</span>
                      </Button>
                    </label>
                    <Button size="sm" onClick={() => setShowAddContact(true)} className="text-xs sm:text-sm px-2 sm:px-3">
                      <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-1.5" />
                      <span className="hidden sm:inline">Add</span>
                    </Button>
                  </div>
                </div>
                
                {/* Upload Format Info */}
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs sm:text-sm">
                  <p className="font-medium text-blue-800 mb-1">Excel Upload Format (supports 1000+ contacts):</p>
                  <div className="flex flex-wrap gap-2 text-blue-700">
                    <code className="bg-blue-100 px-2 py-0.5 rounded">Name</code>
                    <code className="bg-blue-100 px-2 py-0.5 rounded">Phone</code>
                    <code className="bg-blue-100 px-2 py-0.5 rounded">Email</code>
                    <code className="bg-blue-100 px-2 py-0.5 rounded">Group</code>
                  </div>
                  <p className="text-blue-600 mt-1 text-[10px] sm:text-xs">Phone format: +919876543210 or 9876543210</p>
                </div>

                {/* Filters - Stack on mobile */}
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search..."
                      value={contactSearch}
                      onChange={(e) => setContactSearch(e.target.value)}
                      className="pl-9 h-9 text-sm"
                    />
                  </div>
                  <div className="flex gap-2">
                    <select
                      value={filterGroup}
                      onChange={(e) => setFilterGroup(e.target.value)}
                      className="h-9 px-2 sm:px-3 border border-slate-300 rounded-md bg-white text-xs sm:text-sm flex-1 sm:flex-none"
                    >
                      <option value="">All Groups</option>
                      {contactGroups.map(g => (
                        <option key={g.id} value={g.name}>{g.name}</option>
                      ))}
                    </select>
                    {selectedContacts.length > 0 && (
                      <Button 
                        onClick={() => { setShowComposeModal(true); }}
                        className="bg-indigo-600 hover:bg-indigo-700 text-xs sm:text-sm px-2 sm:px-3"
                        size="sm"
                      >
                        <Send className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-1.5" />
                        <span className="hidden sm:inline">Message</span> {selectedContacts.length}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-3 sm:px-6">
              {/* Mobile Card View */}
              <div className="sm:hidden space-y-2">
                {filteredContacts.length === 0 ? (
                  <div className="py-8 text-center text-slate-500">
                    <Users className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                    <p className="text-sm">No contacts found</p>
                    <p className="text-xs">Upload Excel or add manually</p>
                  </div>
                ) : (
                  filteredContacts.map(contact => (
                    <div 
                      key={contact.id}
                      className={`p-3 rounded-lg border ${
                        selectedContacts.includes(contact.id) 
                          ? 'bg-indigo-50 border-indigo-200' 
                          : 'bg-white border-slate-200'
                      }`}
                      onClick={() => toggleContactSelection(contact.id)}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={selectedContacts.includes(contact.id)}
                          onChange={() => {}}
                          className="w-4 h-4 rounded border-slate-300 mt-0.5"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="font-medium text-slate-900 text-sm truncate">{contact.name}</p>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                              contact.status === 'active' 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-slate-100 text-slate-600'
                            }`}>
                              {contact.status || 'active'}
                            </span>
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5">{contact.phone || contact.email || '-'}</p>
                          {contact.group && (
                            <span className="inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700">
                              {contact.group}
                            </span>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedContacts([contact.id]);
                              setShowComposeModal(true);
                            }}
                            className="h-7 w-7 p-0"
                          >
                            <Send className="w-3.5 h-3.5 text-indigo-600" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteContact(contact.id);
                            }}
                            className="h-7 w-7 p-0"
                          >
                            <Trash2 className="w-3.5 h-3.5 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Desktop Table View */}
              <div className="hidden sm:block overflow-x-auto border rounded-lg">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectedContacts.length === filteredContacts.length && filteredContacts.length > 0}
                          onChange={selectAllContacts}
                          className="w-4 h-4 rounded border-slate-300"
                        />
                      </th>
                      <th 
                        className="px-4 py-3 text-left text-sm font-semibold text-slate-700 cursor-pointer hover:bg-slate-100"
                        onClick={() => handleSort('name')}
                      >
                        <div className="flex items-center gap-1">
                          Name
                          <ArrowUpDown className={`w-4 h-4 ${sortConfig.key === 'name' ? 'text-indigo-600' : 'text-slate-400'}`} />
                        </div>
                      </th>
                      <th 
                        className="px-4 py-3 text-left text-sm font-semibold text-slate-700 cursor-pointer hover:bg-slate-100"
                        onClick={() => handleSort('phone')}
                      >
                        <div className="flex items-center gap-1">
                          Phone
                          <ArrowUpDown className={`w-4 h-4 ${sortConfig.key === 'phone' ? 'text-indigo-600' : 'text-slate-400'}`} />
                        </div>
                      </th>
                      <th 
                        className="px-4 py-3 text-left text-sm font-semibold text-slate-700 cursor-pointer hover:bg-slate-100"
                        onClick={() => handleSort('email')}
                      >
                        <div className="flex items-center gap-1">
                          Email
                          <ArrowUpDown className={`w-4 h-4 ${sortConfig.key === 'email' ? 'text-indigo-600' : 'text-slate-400'}`} />
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Group</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Status</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredContacts.map(contact => (
                      <tr key={contact.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedContacts.includes(contact.id)}
                            onChange={() => toggleContactSelection(contact.id)}
                            className="w-4 h-4 rounded border-slate-300"
                          />
                        </td>
                        <td className="px-4 py-3 font-medium text-slate-900">{contact.name}</td>
                        <td className="px-4 py-3 text-slate-600">{contact.phone || '-'}</td>
                        <td className="px-4 py-3 text-slate-600">{contact.email || '-'}</td>
                        <td className="px-4 py-3">
                          {contact.group && (
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-indigo-100 text-indigo-700">
                              {contact.group}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            contact.status === 'active' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-slate-100 text-slate-600'
                          }`}>
                            {contact.status || 'active'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                setSelectedContacts([contact.id]);
                                setShowComposeModal(true);
                              }}
                              className="h-8 w-8 p-0"
                            >
                              <Send className="w-4 h-4 text-indigo-600" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleDeleteContact(contact.id)}
                              className="h-8 w-8 p-0"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredContacts.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-4 py-12 text-center text-slate-500">
                          <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                          <p>No contacts found</p>
                          <p className="text-sm">Upload an Excel file or add contacts manually</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <p className="text-xs sm:text-sm text-slate-500 mt-2 px-1">
                {filteredContacts.length} of {contacts.length} contacts
                {selectedContacts.length > 0 && ` • ${selectedContacts.length} selected`}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Quick Send Tab */}
        {activeTab === 'compose' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            {/* Quick Actions */}
            <Card className="lg:col-span-2">
              <CardHeader className="pb-2 sm:pb-3">
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <Send className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" />
                  Quick Send
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 sm:space-y-4 px-3 sm:px-6">
                {/* Channel Selection */}
                <div>
                  <label className="text-xs sm:text-sm font-medium text-slate-700 mb-2 block">Select Channels</label>
                  <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-3">
                    {[
                      { id: 'email', label: 'Email', icon: Mail, color: 'blue' },
                      { id: 'sms', label: 'SMS', icon: Phone, color: 'green' },
                      { id: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, color: 'emerald' },
                      { id: 'telegram', label: 'Telegram', icon: Send, color: 'sky' }
                    ].map(channel => (
                      <button
                        key={channel.id}
                        onClick={() => setComposeData(prev => ({
                          ...prev,
                          channels: { ...prev.channels, [channel.id]: !prev.channels[channel.id] }
                        }))}
                        className={`flex items-center justify-center gap-1.5 sm:gap-2 px-2 sm:px-4 py-2 rounded-lg border-2 transition-all text-xs sm:text-sm ${
                          composeData.channels[channel.id]
                            ? `bg-${channel.color}-100 border-${channel.color}-500 text-${channel.color}-700`
                            : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}
                      >
                        <channel.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        {channel.label}
                        {composeData.channels[channel.id] && <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4" />}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Template Selection */}
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Use Template (Optional)</label>
                  <select
                    value={composeData.selectedTemplate}
                    onChange={(e) => applyTemplate(e.target.value)}
                    className="w-full h-10 px-3 border border-slate-300 rounded-md"
                  >
                    <option value="">Select a template...</option>
                    {templates.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>

                {/* Subject (for email) */}
                {composeData.channels.email && (
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Email Subject</label>
                    <Input
                      placeholder="Enter email subject..."
                      value={composeData.subject}
                      onChange={(e) => setComposeData(prev => ({ ...prev, subject: e.target.value }))}
                    />
                  </div>
                )}

                {/* Message */}
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Message</label>
                  <textarea
                    placeholder="Enter your message... Use {{name}} for personalization"
                    value={composeData.message}
                    onChange={(e) => setComposeData(prev => ({ ...prev, message: e.target.value }))}
                    rows={5}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md resize-none"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Variables: {"{{name}}"}, {"{{product}}"}, {"{{amount}}"}, {"{{date}}"}
                  </p>
                </div>

                {/* Schedule Options */}
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Send Options</label>
                  <div className="flex flex-wrap gap-3">
                    {[
                      { id: 'now', label: 'Send Now' },
                      { id: 'scheduled', label: 'Schedule' },
                      { id: 'recurring', label: 'Recurring' }
                    ].map(opt => (
                      <button
                        key={opt.id}
                        onClick={() => setComposeData(prev => ({ ...prev, scheduleType: opt.id }))}
                        className={`px-4 py-2 rounded-lg border transition-all ${
                          composeData.scheduleType === opt.id
                            ? 'bg-indigo-100 border-indigo-500 text-indigo-700'
                            : 'bg-white border-slate-200 text-slate-600'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {composeData.scheduleType !== 'now' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-2 block">Date</label>
                      <Input
                        type="date"
                        value={composeData.scheduleDate}
                        onChange={(e) => setComposeData(prev => ({ ...prev, scheduleDate: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-2 block">Time</label>
                      <Input
                        type="time"
                        value={composeData.scheduleTime}
                        onChange={(e) => setComposeData(prev => ({ ...prev, scheduleTime: e.target.value }))}
                      />
                    </div>
                    {composeData.scheduleType === 'recurring' && (
                      <div className="col-span-2">
                        <label className="text-sm font-medium text-slate-700 mb-2 block">Repeat</label>
                        <select
                          value={composeData.recurringInterval}
                          onChange={(e) => setComposeData(prev => ({ ...prev, recurringInterval: e.target.value }))}
                          className="w-full h-10 px-3 border border-slate-300 rounded-md"
                        >
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                        </select>
                      </div>
                    )}
                  </div>
                )}

                {/* Send Button */}
                <Button 
                  onClick={() => setShowComposeModal(true)}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-sm"
                  disabled={!composeData.message || Object.values(composeData.channels).every(v => !v)}
                >
                  <Send className="w-4 h-4 mr-2" />
                  Select Recipients & Send
                </Button>
              </CardContent>
            </Card>

            {/* Quick Share Cards - Horizontal scroll on mobile */}
            <div className="lg:space-y-4">
              <div className="flex lg:flex-col gap-3 overflow-x-auto pb-2 lg:pb-0 -mx-3 px-3 lg:mx-0 lg:px-0">
                <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white flex-shrink-0 w-[200px] lg:w-auto">
                  <CardContent className="p-3 sm:p-4">
                    <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-lg flex items-center justify-center">
                        <Image className="w-4 h-4 sm:w-5 sm:h-5" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-sm sm:text-base">Catalog</h3>
                        <p className="text-[10px] sm:text-xs text-white/80">Send product catalog</p>
                      </div>
                    </div>
                    <Button 
                      variant="secondary" 
                      size="sm" 
                      className="w-full bg-white text-blue-600 hover:bg-white/90 text-xs sm:text-sm"
                      onClick={() => {
                        setComposeData(prev => ({ 
                          ...prev, 
                          subject: 'New Product Catalog',
                          message: 'Dear {{name}},\n\nPlease find our latest product catalog attached. Check out our new arrivals and best sellers!\n\nBest regards,\nWINLIFE Team'
                        }));
                        setShowComposeModal(true);
                      }}
                    >
                      <Send className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />
                      Share
                    </Button>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white flex-shrink-0 w-[200px] lg:w-auto">
                  <CardContent className="p-3 sm:p-4">
                    <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-lg flex items-center justify-center">
                        <DollarSign className="w-4 h-4 sm:w-5 sm:h-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold">Share Price List</h3>
                      <p className="text-xs text-white/80">Send updated prices</p>
                    </div>
                  </div>
                  <Button 
                    variant="secondary" 
                    size="sm" 
                    className="w-full bg-white text-green-600 hover:bg-white/90 text-xs sm:text-sm"
                    onClick={() => {
                      setComposeData(prev => ({ 
                        ...prev, 
                        subject: 'Updated Price List',
                        message: 'Dear {{name}},\n\nOur price list has been updated. Please find the new prices effective from today.\n\nFor bulk orders, special discounts are available!\n\nBest regards,\nWINLIFE Team'
                      }));
                      setShowComposeModal(true);
                    }}
                  >
                    <Send className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />
                    Share
                  </Button>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white flex-shrink-0 w-[200px] lg:w-auto">
                <CardContent className="p-3 sm:p-4">
                  <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-lg flex items-center justify-center">
                      <Package className="w-4 h-4 sm:w-5 sm:h-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-sm sm:text-base">Stock Alert</h3>
                      <p className="text-[10px] sm:text-xs text-white/80">Notify stock updates</p>
                    </div>
                  </div>
                  <Button 
                    variant="secondary" 
                    size="sm" 
                    className="w-full bg-white text-purple-600 hover:bg-white/90 text-xs sm:text-sm"
                    onClick={() => {
                      setComposeData(prev => ({ 
                        ...prev, 
                        subject: 'Stock Update Alert',
                        message: 'Dear {{name}},\n\nGood news! The following products are now back in stock:\n\n• {{product}}\n\nOrder now before stocks run out!\n\nBest regards,\nWINLIFE Team'
                      }));
                      setShowComposeModal(true);
                    }}
                  >
                    <Send className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />
                    Share
                  </Button>
                </CardContent>
              </Card>
              </div>

              {/* Quick Marketing - Manual Phone Numbers */}
              <Card className="mt-4 lg:mt-0 border-2 border-dashed border-orange-300 bg-orange-50/50">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-base text-orange-700">
                    <MessageCircle className="w-5 h-5" />
                    Quick WhatsApp Marketing
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-orange-800 mb-1 block">
                      Phone Numbers (one per line or comma-separated)
                    </label>
                    <textarea
                      value={manualPhones}
                      onChange={(e) => setManualPhones(e.target.value)}
                      placeholder="+919876543210&#10;+919876543211&#10;9876543212"
                      rows={4}
                      className="w-full px-3 py-2 border border-orange-300 rounded-md text-sm bg-white focus:border-orange-500 focus:outline-none resize-none"
                    />
                    <p className="text-[10px] text-orange-600 mt-1">
                      {manualPhones.split(/[\s,\n]+/).filter(p => p.trim().length >= 10).length} valid numbers detected
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-orange-800 mb-1 block">Message</label>
                    <textarea
                      value={quickMessage}
                      onChange={(e) => setQuickMessage(e.target.value)}
                      placeholder="Enter your marketing message..."
                      rows={3}
                      className="w-full px-3 py-2 border border-orange-300 rounded-md text-sm bg-white focus:border-orange-500 focus:outline-none resize-none"
                    />
                  </div>
                  <Button
                    onClick={handleSendQuickMarketing}
                    disabled={sendingQuickMessage || !manualPhones.trim() || !quickMessage.trim()}
                    className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                  >
                    {sendingQuickMessage ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send via WhatsApp
                      </>
                    )}
                  </Button>
                  <p className="text-[10px] text-orange-600 text-center">
                    Requires active AiSensy plan & "general_notification" campaign
                  </p>
                </CardContent>
              </Card>

              {/* Quick Telegram Marketing */}
              <Card className="mt-4 lg:mt-0 border-2 border-dashed border-sky-300 bg-sky-50/50">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-base text-sky-700">
                    <Send className="w-5 h-5" />
                    Quick Telegram Marketing
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-sky-800 mb-1 block">
                      Chat IDs (one per line or comma-separated)
                    </label>
                    <textarea
                      value={manualTelegramIds}
                      onChange={(e) => setManualTelegramIds(e.target.value)}
                      placeholder="123456789&#10;987654321&#10;-100123456789"
                      rows={4}
                      className="w-full px-3 py-2 border border-sky-300 rounded-md text-sm bg-white focus:border-sky-500 focus:outline-none resize-none"
                    />
                    <p className="text-[10px] text-sky-600 mt-1">
                      {manualTelegramIds.split(/[\s,\n]+/).filter(id => id.trim().match(/^-?\d+$/)).length} valid IDs detected
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-sky-800 mb-1 block">Message</label>
                    <textarea
                      value={quickTelegramMessage}
                      onChange={(e) => setQuickTelegramMessage(e.target.value)}
                      placeholder="Enter your marketing message..."
                      rows={3}
                      className="w-full px-3 py-2 border border-sky-300 rounded-md text-sm bg-white focus:border-sky-500 focus:outline-none resize-none"
                    />
                  </div>
                  <Button
                    onClick={handleSendQuickTelegram}
                    disabled={sendingQuickTelegram || !manualTelegramIds.trim() || !quickTelegramMessage.trim()}
                    className="w-full bg-sky-600 hover:bg-sky-700 text-white"
                  >
                    {sendingQuickTelegram ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send via Telegram
                      </>
                    )}
                  </Button>
                  <p className="text-[10px] text-sky-600 text-center">
                    Use @userinfobot on Telegram to get Chat IDs
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <Card>
            <CardHeader className="pb-2 sm:pb-3">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" />
                Message History
              </CardTitle>
            </CardHeader>
            <CardContent className="px-3 sm:px-6">
              {/* Mobile Card View for History */}
              <div className="sm:hidden space-y-2">
                {messageHistory.map(msg => (
                  <div key={msg.id} className="p-3 rounded-lg border bg-white">
                    <div className="flex items-start justify-between mb-2">
                      <p className="font-medium text-sm text-slate-900 flex-1 truncate">{msg.subject}</p>
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded-full border ${getChannelColor(msg.channel)}`}>
                        <ChannelIcon channel={msg.channel} size="w-3 h-3" />
                        {msg.channel}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <span>{msg.date}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-green-600">{msg.sent} sent</span>
                        {msg.failed > 0 && <span className="text-red-500">{msg.failed} failed</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Desktop Table View */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Subject</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Channel</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Recipients</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Sent</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Failed</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Date</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {messageHistory.map(msg => (
                      <tr key={msg.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-medium text-slate-900">{msg.subject}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-full border ${getChannelColor(msg.channel)}`}>
                            <ChannelIcon channel={msg.channel} size="w-3 h-3" />
                            {msg.channel}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center text-slate-600">{msg.recipients}</td>
                        <td className="px-4 py-3 text-center text-green-600 font-medium">{msg.sent}</td>
                        <td className="px-4 py-3 text-center text-red-600 font-medium">{msg.failed}</td>
                        <td className="px-4 py-3 text-slate-600">{msg.date}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            msg.status === 'completed' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-amber-100 text-amber-700'
                          }`}>
                            {msg.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Scheduled Tab */}
        {activeTab === 'scheduled' && (
          <Card>
            <CardHeader className="pb-2 sm:pb-3">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" />
                Scheduled
              </CardTitle>
            </CardHeader>
            <CardContent className="px-3 sm:px-6">
              {/* Mobile Card View for Scheduled */}
              <div className="sm:hidden space-y-2">
                {scheduledMessages.length === 0 ? (
                  <div className="py-8 text-center text-slate-500">
                    <Calendar className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                    <p className="text-sm">No scheduled messages</p>
                  </div>
                ) : (
                  scheduledMessages.map(msg => (
                    <div key={msg.id} className="p-3 rounded-lg border bg-white">
                      <div className="flex items-start justify-between mb-2">
                        <p className="font-medium text-sm text-slate-900 flex-1 truncate">{msg.subject}</p>
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded-full border ${getChannelColor(msg.channel)}`}>
                          <ChannelIcon channel={msg.channel} size="w-3 h-3" />
                          {msg.channel}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-slate-500 mb-2">
                        <span>{msg.scheduledFor}</span>
                        <span className="px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 text-[10px]">{msg.recurring}</span>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" className="flex-1 h-7 text-xs">
                          <Edit2 className="w-3 h-3 mr-1" />Edit
                        </Button>
                        <Button variant="outline" size="sm" className="h-7 text-xs text-red-500 border-red-200">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              {/* Desktop Table View */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Subject</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Channel</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Recipients</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Scheduled For</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Recurring</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {scheduledMessages.map(msg => (
                      <tr key={msg.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-medium text-slate-900">{msg.subject}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-full border ${getChannelColor(msg.channel)}`}>
                            <ChannelIcon channel={msg.channel} size="w-3 h-3" />
                            {msg.channel}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center text-slate-600">{msg.recipients}</td>
                        <td className="px-4 py-3 text-slate-600">{msg.scheduledFor}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-700">
                            {msg.recurring}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <Edit2 className="w-4 h-4 text-slate-600" />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {scheduledMessages.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-4 py-12 text-center text-slate-500">
                          <Calendar className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                          <p>No scheduled messages</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Templates Tab */}
        {activeTab === 'templates' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {templates.map(template => (
              <Card key={template.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-3 sm:p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-slate-900">{template.name}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        template.type === 'marketing' ? 'bg-blue-100 text-blue-700' :
                        template.type === 'utility' ? 'bg-green-100 text-green-700' :
                        template.type === 'transactional' ? 'bg-purple-100 text-purple-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>
                        {template.type}
                      </span>
                    </div>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-sm text-slate-600 mb-4 line-clamp-3">{template.message}</p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full"
                    onClick={() => {
                      applyTemplate(template.id);
                      setActiveTab('compose');
                    }}
                  >
                    <Send className="w-4 h-4 mr-1.5" />
                    Use Template
                  </Button>
                </CardContent>
              </Card>
            ))}
            <Card className="border-dashed border-2 hover:border-indigo-300 transition-colors cursor-pointer">
              <CardContent className="p-4 flex flex-col items-center justify-center h-full min-h-[200px]">
                <Plus className="w-10 h-10 text-slate-400 mb-2" />
                <p className="text-slate-600 font-medium">Create New Template</p>
                <p className="text-xs text-slate-400">Add your custom template</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-4 sm:space-y-6">
            {/* WhatsApp Configuration */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <MessageCircle className="w-5 h-5 text-emerald-600" />
                  WhatsApp Integration (AiSensy)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {whatsappConfig?.configured ? (
                  <>
                    <div className="flex items-center gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                      <div>
                        <p className="font-medium text-emerald-800">WhatsApp Connected</p>
                        <p className="text-sm text-emerald-600">
                          {whatsappConfig.business_name} • {whatsappConfig.whatsapp_number}
                        </p>
                      </div>
                    </div>
                    
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="p-4 bg-slate-50 rounded-lg border">
                        <p className="text-xs text-slate-500 uppercase font-medium">Provider</p>
                        <p className="text-lg font-semibold text-slate-900">{whatsappConfig.provider}</p>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-lg border">
                        <p className="text-xs text-slate-500 uppercase font-medium">WhatsApp Number</p>
                        <p className="text-lg font-semibold text-slate-900">{whatsappConfig.whatsapp_number}</p>
                      </div>
                    </div>

                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium text-amber-800">Important: Create Templates in AiSensy</p>
                          <p className="text-sm text-amber-700 mt-1">
                            To send WhatsApp messages, you need to create and get templates approved in your AiSensy dashboard:
                          </p>
                          <ol className="text-sm text-amber-700 mt-2 space-y-1 list-decimal list-inside">
                            <li>Go to <a href="https://app.aisensy.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-amber-900">app.aisensy.com</a></li>
                            <li>Navigate to "Templates" → "Create Template"</li>
                            <li>Create templates for: catalog sharing, price updates, stock alerts</li>
                            <li>Submit templates for approval (usually 24-48 hours)</li>
                            <li>Once approved, create "Campaigns" using these templates</li>
                          </ol>
                          <p className="text-sm text-amber-700 mt-2">
                            <strong>Campaign name must match</strong> what you use in the app.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="font-medium text-blue-800 mb-2">Recommended Templates to Create:</p>
                      <ul className="text-sm text-blue-700 space-y-1">
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          <code className="bg-blue-100 px-1 rounded">general_notification</code> - For general messages
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          <code className="bg-blue-100 px-1 rounded">catalog_share</code> - For sharing catalogs
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          <code className="bg-blue-100 px-1 rounded">price_update</code> - For price list updates
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          <code className="bg-blue-100 px-1 rounded">stock_alert</code> - For stock notifications
                        </li>
                      </ul>
                    </div>

                    {/* Quick Test Section */}
                    <div className="p-4 bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-3">
                        <Send className="w-5 h-5 text-emerald-600" />
                        <p className="font-semibold text-emerald-800">Quick Test WhatsApp</p>
                      </div>
                      <p className="text-sm text-emerald-700 mb-3">
                        Send a test message to verify your integration is working.
                        <br />
                        <span className="text-xs text-emerald-600">(Requires <code className="bg-emerald-100 px-1 rounded">general_notification</code> template in AiSensy)</span>
                      </p>
                      <div className="space-y-3">
                        <div>
                          <label className="text-sm font-medium text-emerald-800 mb-1 block">Phone Number</label>
                          <Input
                            placeholder="+91 9876543210"
                            value={testPhone}
                            onChange={(e) => setTestPhone(e.target.value)}
                            className="bg-white border-emerald-300 focus:border-emerald-500"
                          />
                          <p className="text-xs text-emerald-600 mt-1">Include country code (e.g., +91 for India)</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-emerald-800 mb-1 block">Test Message</label>
                          <textarea
                            value={testMessage}
                            onChange={(e) => setTestMessage(e.target.value)}
                            rows={2}
                            className="w-full px-3 py-2 border border-emerald-300 rounded-md text-sm bg-white focus:border-emerald-500 focus:outline-none resize-none"
                          />
                        </div>
                        <Button
                          onClick={handleSendTestWhatsApp}
                          disabled={sendingTest || !testPhone}
                          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                          {sendingTest ? (
                            <>
                              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                              Sending...
                            </>
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-2" />
                              Send Test Message
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-center gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-600" />
                      <div>
                        <p className="font-medium text-amber-800">WhatsApp Not Configured</p>
                        <p className="text-sm text-amber-600">Contact admin to set up AiSensy API key</p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Telegram Configuration */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <Send className="w-5 h-5 text-sky-600" />
                  Telegram Integration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {telegramConfig?.configured ? (
                  <>
                    <div className="flex items-center gap-3 p-3 bg-sky-50 border border-sky-200 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-sky-600" />
                      <div>
                        <p className="font-medium text-sky-800">Telegram Bot Connected</p>
                        <p className="text-sm text-sky-600">
                          {telegramConfig.bot_username} {telegramConfig.bot_info?.first_name ? `• ${telegramConfig.bot_info.first_name}` : ''}
                        </p>
                      </div>
                    </div>
                    
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="p-4 bg-slate-50 rounded-lg border">
                        <p className="text-xs text-slate-500 uppercase font-medium">Provider</p>
                        <p className="text-lg font-semibold text-slate-900">{telegramConfig.provider}</p>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-lg border">
                        <p className="text-xs text-slate-500 uppercase font-medium">Bot Username</p>
                        <p className="text-lg font-semibold text-slate-900">{telegramConfig.bot_username}</p>
                      </div>
                    </div>

                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium text-blue-800">How to get Chat ID</p>
                          <p className="text-sm text-blue-700 mt-1">
                            To send messages to a user or group, you need their Chat ID:
                          </p>
                          <ol className="text-sm text-blue-700 mt-2 space-y-1 list-decimal list-inside">
                            <li>User must start a conversation with your bot first</li>
                            <li>Send any message to the bot</li>
                            <li>Use <a href="https://t.me/userinfobot" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-900">@userinfobot</a> to get your Chat ID</li>
                            <li>For groups, add the bot to the group and check group info</li>
                          </ol>
                        </div>
                      </div>
                    </div>

                    {/* Quick Test Section */}
                    <div className="p-4 bg-gradient-to-br from-sky-50 to-blue-50 border border-sky-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-3">
                        <Send className="w-5 h-5 text-sky-600" />
                        <p className="font-semibold text-sky-800">Quick Test Telegram</p>
                      </div>
                      <p className="text-sm text-sky-700 mb-3">
                        Send a test message to verify your Telegram integration is working.
                      </p>
                      <div className="space-y-3">
                        <div>
                          <label className="text-sm font-medium text-sky-800 mb-1 block">Chat ID</label>
                          <Input
                            placeholder="e.g., 123456789"
                            value={testChatId}
                            onChange={(e) => setTestChatId(e.target.value)}
                            className="bg-white border-sky-300 focus:border-sky-500"
                          />
                          <p className="text-xs text-sky-600 mt-1">Enter user or group chat ID</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-sky-800 mb-1 block">Test Message</label>
                          <textarea
                            value={testTelegramMessage}
                            onChange={(e) => setTestTelegramMessage(e.target.value)}
                            rows={2}
                            className="w-full px-3 py-2 border border-sky-300 rounded-md text-sm bg-white focus:border-sky-500 focus:outline-none resize-none"
                          />
                        </div>
                        <Button
                          onClick={handleSendTestTelegram}
                          disabled={sendingTelegramTest || !testChatId}
                          className="w-full bg-sky-600 hover:bg-sky-700 text-white"
                        >
                          {sendingTelegramTest ? (
                            <>
                              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                              Sending...
                            </>
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-2" />
                              Send Test Message
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-center gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-600" />
                      <div>
                        <p className="font-medium text-amber-800">Telegram Not Configured</p>
                        <p className="text-sm text-amber-600">Contact admin to set up Telegram Bot Token</p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Other Channels Status */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <Settings className="w-5 h-5 text-slate-600" />
                  Other Channels
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 rounded-lg border flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Mail className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Email (SendGrid)</p>
                      <p className="text-xs text-amber-600">Pending Setup</p>
                    </div>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg border flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <Phone className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">SMS (Twilio)</p>
                      <p className="text-xs text-amber-600">Pending Setup</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Add Contact Modal */}
      {showAddContact && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900">Add New Contact</h3>
                <Button variant="ghost" size="sm" onClick={() => setShowAddContact(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Name *</label>
                  <Input
                    placeholder="Contact name"
                    value={newContact.name}
                    onChange={(e) => setNewContact(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Phone</label>
                  <Input
                    placeholder="+91 9876543210"
                    value={newContact.phone}
                    onChange={(e) => setNewContact(prev => ({ ...prev, phone: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Email</label>
                  <Input
                    type="email"
                    placeholder="email@example.com"
                    value={newContact.email}
                    onChange={(e) => setNewContact(prev => ({ ...prev, email: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Telegram ID</label>
                  <Input
                    placeholder="123456789"
                    value={newContact.telegram_id}
                    onChange={(e) => setNewContact(prev => ({ ...prev, telegram_id: e.target.value }))}
                  />
                  <p className="text-xs text-slate-500 mt-1">Use @userinfobot to get your ID</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Group</label>
                  <select
                    value={newContact.group}
                    onChange={(e) => setNewContact(prev => ({ ...prev, group: e.target.value }))}
                    className="w-full h-10 px-3 border border-slate-300 rounded-md"
                  >
                    <option value="">Select group...</option>
                    {contactGroups.map(g => (
                      <option key={g.id} value={g.name}>{g.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <Button variant="outline" className="flex-1" onClick={() => setShowAddContact(false)}>
                  Cancel
                </Button>
                <Button className="flex-1 bg-indigo-600 hover:bg-indigo-700" onClick={handleAddContact}>
                  <Plus className="w-4 h-4 mr-1.5" />
                  Add Contact
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Compose Modal - Select Recipients */}
      {showComposeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Select Recipients</h3>
                <Button variant="ghost" size="sm" className="text-white hover:bg-white/20" onClick={() => setShowComposeModal(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              <div className="mb-4">
                <Input
                  placeholder="Search contacts..."
                  value={contactSearch}
                  onChange={(e) => setContactSearch(e.target.value)}
                  className="mb-3"
                />
                <div className="flex gap-2 mb-3">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={selectAllContacts}
                  >
                    {selectedContacts.length === filteredContacts.length ? 'Deselect All' : 'Select All'}
                  </Button>
                  {contactGroups.map(g => (
                    <Button
                      key={g.id}
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const groupContacts = contacts.filter(c => c.group === g.name).map(c => c.id);
                        setSelectedContacts(prev => {
                          const allSelected = groupContacts.every(id => prev.includes(id));
                          if (allSelected) {
                            return prev.filter(id => !groupContacts.includes(id));
                          } else {
                            return [...new Set([...prev, ...groupContacts])];
                          }
                        });
                      }}
                    >
                      {g.name}
                    </Button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                {filteredContacts.map(contact => (
                  <div
                    key={contact.id}
                    onClick={() => toggleContactSelection(contact.id)}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                      selectedContacts.includes(contact.id)
                        ? 'bg-indigo-50 border-indigo-300'
                        : 'bg-white border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedContacts.includes(contact.id)}
                      onChange={() => {}}
                      className="w-4 h-4 rounded border-slate-300"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{contact.name}</p>
                      <p className="text-sm text-slate-500">
                        {contact.phone && <span className="mr-3">{contact.phone}</span>}
                        {contact.email && <span>{contact.email}</span>}
                      </p>
                    </div>
                    {contact.group && (
                      <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-600">
                        {contact.group}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="border-t px-6 py-4 bg-slate-50">
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-600">
                  {selectedContacts.length} contacts selected
                </p>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setShowComposeModal(false)}>
                    Cancel
                  </Button>
                  <Button 
                    className="bg-indigo-600 hover:bg-indigo-700"
                    onClick={handleSendMessage}
                    disabled={selectedContacts.length === 0 || loading}
                  >
                    {loading ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-1.5" />
                        Send to {selectedContacts.length} Recipients
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Messaging;
